import React from 'react';
import { Button, Chip, Select, TextInput } from '@origo/origo-ui';
import { MagicWandIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import {
    type CodedValueInfo,
    type UnmappedValuePolicy,
    UNMAPPED_POLICY_LABEL,
    type ValueMapping,
} from '../../../api/types';
import { suggestValueMap } from './draft';
import styles from './create.module.css';

interface ValueMapEditorProps {
    valueMap: ValueMapping[];
    unmatchedCodes: string[] | null;
    onUnmapped: UnmappedValuePolicy;
    /** NGIS-feltets kodeliste; gir nedtrekk i NGIS-kolonnen. Fritekst når fraværende. */
    ngisCodes?: CodedValueInfo[] | null;
    /** AGOL-feltets kodeliste; gir nedtrekk i AGOL-kolonnen. Fritekst når fraværende. */
    agolCodes?: CodedValueInfo[] | null;
    onChange: (valueMap: ValueMapping[]) => void;
    onPolicyChange: (policy: UnmappedValuePolicy) => void;
}

const POLICY_OPTIONS = Object.entries(UNMAPPED_POLICY_LABEL).map(([value, label]) => ({
    value,
    label,
}));

/** Nedtrekksvalg for en kodeliste. Beholder en lagret verdi som ikke (lenger) finnes i listen. */
function codeOptions(codes: CodedValueInfo[], current: string) {
    const options = [
        { value: '', label: '— velg —' },
        ...codes.map((c) => ({ value: c.code, label: `${c.code} – ${c.name}` })),
    ];
    if (current && !codes.some((c) => c.code === current)) {
        options.push({ value: current, label: `${current} (ukjent kode)` });
    }
    return options;
}

/** Redigerer kode-par (AGOL ↔ NGIS) og policy for ukjente verdier på ett felt. */
export const ValueMapEditor: React.FC<ValueMapEditorProps> = ({
    valueMap,
    unmatchedCodes,
    onUnmapped,
    ngisCodes,
    agolCodes,
    onChange,
    onPolicyChange,
}) => {
    const hasNgisCodes = !!ngisCodes && ngisCodes.length > 0;
    const hasAgolCodes = !!agolCodes && agolCodes.length > 0;

    const updatePair = (index: number, patch: Partial<ValueMapping>) => {
        onChange(valueMap.map((pair, i) => (i === index ? { ...pair, ...patch } : pair)));
    };

    const removePair = (index: number) => {
        onChange(valueMap.filter((_, i) => i !== index));
    };

    const addPair = () => {
        onChange([...valueMap, { agolValue: '', ngisValue: '' }]);
    };

    // Legger til navne-matchede par for NGIS-koder som ikke allerede har et par.
    const addSuggestions = () => {
        if (!hasNgisCodes || !hasAgolCodes) return;
        const used = new Set(valueMap.map((p) => p.ngisValue));
        const { valueMap: suggested } = suggestValueMap(ngisCodes, agolCodes);
        onChange([...valueMap, ...suggested.filter((p) => !used.has(p.ngisValue))]);
    };

    // Med NGIS-kodelisten tilgjengelig regnes manglende koder ut fra gjeldende par, så tallet
    // følger redigeringen. Ellers vises preflight-rapportens liste som før.
    const missing = hasNgisCodes
        ? ngisCodes
              .filter((c) => !valueMap.some((p) => p.ngisValue === c.code))
              .map((c) => `${c.code} – ${c.name}`)
        : unmatchedCodes ?? [];

    return (
        <div className={styles.valueMap}>
            <p className={styles.muted}>Domene / verdimap (AGOL-kode → NGIS-kode)</p>

            {valueMap.length === 0 && (
                <p className={styles.muted}>
                    Ingen kode-par ennå. Legg til par for kodene som skal oversettes mellom AGOL og NGIS.
                </p>
            )}

            {valueMap.map((pair, i) => (
                <div key={i} className={styles.valueMapRow}>
                    {hasAgolCodes ? (
                        <Select
                            label="AGOL-verdi"
                            hideLabel
                            value={pair.agolValue}
                            options={codeOptions(agolCodes, pair.agolValue)}
                            onChange={(e) => updatePair(i, { agolValue: e.target.value })}
                        />
                    ) : (
                        <TextInput
                            label="AGOL-verdi"
                            hideLabel
                            placeholder="AGOL-kode"
                            value={pair.agolValue}
                            onChange={(e) => updatePair(i, { agolValue: e.target.value })}
                        />
                    )}
                    {hasNgisCodes ? (
                        <Select
                            label="NGIS-verdi"
                            hideLabel
                            value={pair.ngisValue}
                            options={codeOptions(ngisCodes, pair.ngisValue)}
                            onChange={(e) => updatePair(i, { ngisValue: e.target.value })}
                        />
                    ) : (
                        <TextInput
                            label="NGIS-verdi"
                            hideLabel
                            placeholder="NGIS-kode"
                            value={pair.ngisValue}
                            onChange={(e) => updatePair(i, { ngisValue: e.target.value })}
                        />
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Fjern verdipar"
                        onClick={() => removePair(i)}
                    >
                        <TrashIcon size={16} />
                    </Button>
                </div>
            ))}

            <div className={styles.fieldMeta}>
                <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<PlusIcon size={14} weight="bold" />}
                    onClick={addPair}
                >
                    Legg til par
                </Button>
                {hasNgisCodes && hasAgolCodes && (
                    <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<MagicWandIcon size={14} weight="bold" />}
                        onClick={addSuggestions}
                    >
                        Foreslå på navn
                    </Button>
                )}
            </div>

            {missing.length > 0 && (
                <div className={styles.fieldMeta}>
                    <span className={styles.muted}>
                        {hasNgisCodes ? 'NGIS-koder uten par:' : 'Uten treff i AGOL:'}
                    </span>
                    {missing.map((code) => (
                        <Chip key={code} variant="warning">
                            {code}
                        </Chip>
                    ))}
                </div>
            )}

            <Select
                label="Ved ukjent verdi"
                value={String(onUnmapped)}
                onChange={(e) => onPolicyChange(Number(e.target.value) as UnmappedValuePolicy)}
                options={POLICY_OPTIONS}
            />
        </div>
    );
};
